import type { ReactNode } from 'react';

export interface Column<T> {
  key: string;
  label: string;
  sortable?: boolean;
  render: (row: T) => ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  sortColumn?: string;
  sortDirection?: 'ASC' | 'DESC';
  onSortChange?: (column: string) => void;
  emptyMessage?: string;
}

export function DataTable<T>(props: DataTableProps<T>) {
  if (props.rows.length === 0) {
    return <div className="empty">{props.emptyMessage ?? 'No records.'}</div>;
  }
  return (
    <table className="table">
      <thead>
        <tr>
          {props.columns.map((c) => {
            const isSorted = c.sortable && props.sortColumn === c.key;
            const arrow = isSorted ? (props.sortDirection === 'ASC' ? ' ▲' : ' ▼') : '';
            return (
              <th
                key={c.key}
                className={`${c.sortable ? 'sortable' : ''} ${c.className ?? ''}`}
                onClick={c.sortable ? () => props.onSortChange?.(c.key) : undefined}
              >
                {c.label}
                {arrow}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {props.rows.map((row) => (
          <tr key={props.rowKey(row)}>
            {props.columns.map((c) => (
              <td key={c.key} className={c.className}>
                {c.render(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
